import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { Home, ClipboardList, User, Bell, CalendarClock, CalendarDays, Wallet, GraduationCap, CalendarOff } from "lucide-react";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { PortalSwitcher } from "../components/PortalSwitcher";
import { BrandLogo } from "../components/BrandLogo";
import { useAuth } from "../hooks/useAuth";
import { useLocalization } from "../hooks/useLocalization";

export const EmployeeLayout = () => {
  const { user, logout } = useAuth();
  const { t } = useLocalization();
  const location = useLocation();
  // Mirrors ManagementLayout: clicking the already-active bottom-nav tab refreshes the
  // current page's data (remounts the routed page) instead of doing nothing.
  const [refreshKey, setRefreshKey] = useState(0);

  const tabs = [
    { to: "/employee", label: t("nav.home"), icon: Home, testId: "tab-home", end: true },
    { to: "/employee/onboarding", label: t("nav.myOnboarding"), icon: ClipboardList, testId: "tab-onboarding" },
    { to: "/employee/availability", label: t("nav.myAvailability"), icon: CalendarClock, testId: "tab-availability" },
    { to: "/employee/schedule", label: t("nav.mySchedule"), icon: CalendarDays, testId: "tab-schedule" },
    { to: "/employee/payroll", label: t("nav.myPayroll"), icon: Wallet, testId: "tab-payroll" },
    { to: "/employee/training", label: t("nav.myTraining"), icon: GraduationCap, testId: "tab-training" },
    { to: "/employee/leave", label: t("nav.myLeave"), icon: CalendarOff, testId: "tab-leave" },
    { to: "/employee/notifications", label: t("nav.myNotifications"), icon: Bell, testId: "tab-notifications" },
    { to: "/employee/profile", label: t("nav.myProfile"), icon: User, testId: "tab-profile" },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-20" data-testid="employee-layout">
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-white/90 border-b border-gray-200 px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BrandLogo />
          <p className="text-xs text-gray-500 hidden sm:block">{user?.name}</p>
        </div>
        <div className="flex items-center gap-2">
          <PortalSwitcher />
          <LanguageSwitcher />
          <button data-testid="employee-logout-button" onClick={logout} className="text-xs font-semibold text-gray-500 px-2">
            {t("auth.logout")}
          </button>
        </div>
      </header>
      <main className="px-5 py-6 max-w-2xl mx-auto">
        <Outlet key={refreshKey} />
      </main>
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex justify-around py-2 z-30 overflow-x-auto">
        {tabs.map(({ to, label, icon: Icon, testId, end }) => {
          const isCurrent = end ? location.pathname === to : location.pathname.startsWith(to);
          return (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testId}
              onClick={(e) => {
                if (isCurrent) {
                  e.preventDefault();
                  setRefreshKey((k) => k + 1);
                }
              }}
              className={() =>
                `flex flex-col items-center gap-1 px-2 py-1.5 rounded-xl text-[10px] font-medium transition-colors shrink-0 ${
                  isCurrent ? "text-blue-600" : "text-gray-500"
                }`
              }
            >
              <Icon size={18} />
              {label}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
};
